import { z } from 'zod';
import { EasingSchema, IdSchema, MAX_ANIMATION_SECONDS, MOTION_TEMPLATE_IDS, SCENE_COORD_LIMIT, type Easing } from '../../domain/schemas';

/**
 * Procedural motion specification (Phase 3C).
 *
 * A `MotionPlan` describes motion in terms of named target *slots* ("hero", "sign") and
 * timed *steps* that apply a template from the motion library to one or more slots. Plans
 * contain no object ids: a `Binding` maps slots to stable object ids when the plan is
 * compiled, which is what makes recipes portable. Times are seconds, so a plan works at any
 * frame rate; the compiler derives frames by rounding.
 *
 * This is deterministic procedural generation from fixed templates — not neural or
 * "AI" generation. The same plan, binding, seed and geometry always give the same keys.
 */

export const MOTION_PLAN_VERSION = 1 as const;
export const MAX_STEPS = 32;
export const MAX_TARGETS = 64;
export const MAX_REPEAT = 64;
export const MAX_PATH_POINTS = 64;
export const MAX_GENERATED_KEYS = 4000;
export const MAX_TRAIL_POINTS = 240;
/** Most keys one path repetition produces (see the sampling policy in paths.ts). */
export const MAX_PATH_KEYS = 60;

/** Template ids live with the project schema (motion cues of audio-visual sessions refer to them). */
export const TEMPLATE_IDS = MOTION_TEMPLATE_IDS;
export const TemplateIdSchema = z.enum(TEMPLATE_IDS);
export type TemplateId = z.infer<typeof TemplateIdSchema>;

export type ParamValue = number | string | boolean | null;

export type ParamDef =
  | { key: string; label: string; kind: 'number'; min: number; max: number; step: number; default: number | null; unit?: string; help?: string }
  | { key: string; label: string; kind: 'enum'; options: readonly string[]; labels?: Record<string, string>; default: string; help?: string }
  | { key: string; label: string; kind: 'boolean'; default: boolean; help?: string };

export interface TemplateInfo {
  label: string;
  group: 'character' | 'object';
  description: string;
  /** Only character instances (it poses limbs). */
  characterOnly: boolean;
  /** Seconds of one repetition (or one cycle) when no duration is given. */
  defaultSeconds: number;
  /** Cycles repeat inside the duration; otherwise repetitions run back to back. */
  cyclic: boolean;
  /** Seconds of one cycle (cyclic templates): "repeat 4 times" lasts 4 cycles. */
  cycleSeconds?: number;
  /** May be stretched to fill a plan's total duration when it has no duration of its own. */
  flexible: boolean;
  defaultEasing: Easing;
  params: readonly ParamDef[];
}

const intensity: ParamDef = { key: 'intensity', label: 'Intensity', kind: 'number', min: 0.1, max: 3, step: 0.1, default: 1, help: 'Scales the size of the motion' };
const travel = (def: string): ParamDef => ({ key: 'direction', label: 'Direction', kind: 'enum', options: ['right', 'left', 'none'], labels: { none: 'In place' }, default: def });
const distance = (help: string): ParamDef => ({ key: 'distance', label: 'Distance', kind: 'number', min: 0, max: SCENE_COORD_LIMIT, step: 10, default: null, unit: 'px', help });

export const TEMPLATES: Record<TemplateId, TemplateInfo> = {
  idle: { label: 'Idle breathing', group: 'character', description: 'Breathing: torso rises, head and arms sway slightly', characterOnly: true, defaultSeconds: 2.4, cyclic: true, cycleSeconds: 2.4, flexible: true, defaultEasing: 'ease-in-out', params: [intensity] },
  walk: {
    label: 'Walking',
    group: 'character',
    description: 'Walk cycle with coordinated arms and legs; travels without foot sliding',
    characterOnly: true,
    defaultSeconds: 2,
    cyclic: true, cycleSeconds: 1,
    flexible: true,
    defaultEasing: 'linear',
    params: [
      travel('right'),
      distance('Empty: natural walking speed'),
      { key: 'stride', label: 'Stride', kind: 'number', min: 0.25, max: 2, step: 0.05, default: 1, help: 'Leg swing amplitude' },
      { key: 'frequency', label: 'Cycles / s', kind: 'number', min: 0.3, max: 4, step: 0.05, default: null, help: 'Empty: chosen from the distance' },
    ],
  },
  run: {
    label: 'Running',
    group: 'character',
    description: 'Run cycle: wider, faster swings and a forward lean; travels without foot sliding',
    characterOnly: true,
    defaultSeconds: 2,
    cyclic: true, cycleSeconds: 0.6,
    flexible: true,
    defaultEasing: 'linear',
    params: [
      travel('right'),
      distance('Empty: natural running speed'),
      { key: 'stride', label: 'Stride', kind: 'number', min: 0.25, max: 2, step: 0.05, default: 1, help: 'Leg swing amplitude' },
      { key: 'frequency', label: 'Cycles / s', kind: 'number', min: 0.3, max: 5, step: 0.05, default: null, help: 'Empty: chosen from the distance' },
    ],
  },
  jump: {
    label: 'Jumping',
    group: 'character',
    description: 'Anticipation crouch, arc, landing compression and recovery (any object)',
    characterOnly: false,
    defaultSeconds: 1,
    cyclic: false,
    flexible: false,
    defaultEasing: 'linear',
    params: [
      { key: 'height', label: 'Height', kind: 'number', min: 0.05, max: 3, step: 0.05, default: 0.6, unit: '× height' },
      travel('none'),
      distance('Horizontal travel during the jump'),
      { key: 'squash', label: 'Squash (objects)', kind: 'boolean', default: true },
    ],
  },
  land: { label: 'Landing', group: 'character', description: 'Landing compression and recovery', characterOnly: true, defaultSeconds: 0.5, cyclic: false, flexible: false, defaultEasing: 'ease-out', params: [intensity] },
  crouch: {
    label: 'Crouching',
    group: 'character',
    description: 'Crouch down, hold, stand up',
    characterOnly: true,
    defaultSeconds: 1,
    cyclic: false,
    flexible: false,
    defaultEasing: 'ease-in-out',
    params: [intensity, { key: 'hold', label: 'Hold', kind: 'number', min: 0, max: 0.9, step: 0.05, default: 0.4, help: 'Fraction of the step spent down' }],
  },
  wave: {
    label: 'Waving',
    group: 'character',
    description: 'Raise an arm, wave, lower it',
    characterOnly: true,
    defaultSeconds: 1.6,
    cyclic: false,
    flexible: false,
    defaultEasing: 'ease-in-out',
    params: [
      { key: 'arm', label: 'Arm', kind: 'enum', options: ['right', 'left'], default: 'right' },
      { key: 'waves', label: 'Waves', kind: 'number', min: 1, max: 10, step: 1, default: 3 },
      intensity,
    ],
  },
  turn: {
    label: 'Turning',
    group: 'character',
    description: 'A half turn: the body narrows and widens again (facing itself is a base property)',
    characterOnly: false,
    defaultSeconds: 0.6,
    cyclic: false,
    flexible: false,
    defaultEasing: 'ease-in-out',
    params: [],
  },
  celebrate: {
    label: 'Celebrating',
    group: 'character',
    description: 'Arms up with little hops',
    characterOnly: true,
    defaultSeconds: 1.4,
    cyclic: false,
    flexible: false,
    defaultEasing: 'ease-out',
    params: [{ key: 'hops', label: 'Hops', kind: 'number', min: 0, max: 8, step: 1, default: 2 }, intensity],
  },
  fall: {
    label: 'Falling',
    group: 'character',
    description: 'Tip over and lie down (does not get up)',
    characterOnly: false,
    defaultSeconds: 0.8,
    cyclic: false,
    flexible: false,
    defaultEasing: 'ease-in',
    params: [{ key: 'direction', label: 'Direction', kind: 'enum', options: ['right', 'left'], default: 'right' }],
  },
  float: { label: 'Floating', group: 'object', description: 'Slow hover up and down with a slight tilt', characterOnly: false, defaultSeconds: 3, cyclic: true, cycleSeconds: 3, flexible: true, defaultEasing: 'ease-in-out', params: [intensity] },
  bounce: {
    label: 'Bouncing',
    group: 'object',
    description: 'Hops up and falls back, optionally squashing on contact',
    characterOnly: false,
    defaultSeconds: 0.7,
    cyclic: true, cycleSeconds: 0.7,
    flexible: true,
    defaultEasing: 'ease-out',
    params: [
      { key: 'height', label: 'Height', kind: 'number', min: 0.05, max: 3, step: 0.05, default: 0.4, unit: '× height' },
      { key: 'squash', label: 'Squash', kind: 'boolean', default: true },
      { key: 'decay', label: 'Decay', kind: 'boolean', default: false, help: 'Each bounce lower than the last' },
    ],
  },
  spin: {
    label: 'Spinning',
    group: 'object',
    description: 'Full turns around the anchor',
    characterOnly: false,
    defaultSeconds: 2,
    cyclic: true, cycleSeconds: 2,
    flexible: true,
    defaultEasing: 'linear',
    params: [
      { key: 'direction', label: 'Direction', kind: 'enum', options: ['clockwise', 'counterclockwise'], default: 'clockwise' },
      { key: 'turns', label: 'Turns per cycle', kind: 'number', min: 0.25, max: 20, step: 0.25, default: 1 },
    ],
  },
  shake: {
    label: 'Shaking',
    group: 'object',
    description: 'Fast side-to-side jitter',
    characterOnly: false,
    defaultSeconds: 0.5,
    cyclic: false,
    flexible: false,
    defaultEasing: 'linear',
    params: [
      { key: 'amplitude', label: 'Amplitude', kind: 'number', min: 1, max: 400, step: 1, default: 8, unit: 'px' },
      { key: 'frequency', label: 'Shakes / s', kind: 'number', min: 1, max: 30, step: 1, default: 12 },
      { key: 'axis', label: 'Axis', kind: 'enum', options: ['x', 'y', 'both'], default: 'x' },
      { key: 'decay', label: 'Decay', kind: 'boolean', default: true },
    ],
  },
  pulse: {
    label: 'Pulsing',
    group: 'object',
    description: 'Grows and shrinks around the anchor; optionally flickers',
    characterOnly: false,
    defaultSeconds: 0.6,
    cyclic: true, cycleSeconds: 0.6,
    flexible: true,
    defaultEasing: 'ease-in-out',
    params: [
      { key: 'amount', label: 'Scale amount', kind: 'number', min: 0.01, max: 2, step: 0.01, default: 0.15 },
      { key: 'flicker', label: 'Flicker opacity', kind: 'boolean', default: false },
    ],
  },
  fadeIn: {
    label: 'Fading in',
    group: 'object',
    description: 'Opacity from 0 to its base (hidden objects become visible)',
    characterOnly: false,
    defaultSeconds: 1,
    cyclic: false,
    flexible: false,
    defaultEasing: 'ease-out',
    params: [{ key: 'to', label: 'Final opacity', kind: 'number', min: 0.05, max: 1, step: 0.05, default: null, help: 'Empty: the base opacity' }],
  },
  fadeOut: { label: 'Fading out', group: 'object', description: 'Opacity down to 0', characterOnly: false, defaultSeconds: 1, cyclic: false, flexible: false, defaultEasing: 'ease-in', params: [{ key: 'to', label: 'Final opacity', kind: 'number', min: 0, max: 0.95, step: 0.05, default: 0 }] },
  slide: {
    label: 'Sliding',
    group: 'object',
    description: 'Move by a distance (relative) or to a position (absolute)',
    characterOnly: false,
    defaultSeconds: 1.5,
    cyclic: false,
    flexible: true,
    defaultEasing: 'ease-in-out',
    params: [
      { key: 'mode', label: 'Movement', kind: 'enum', options: ['relative', 'absolute'], default: 'relative' },
      { key: 'direction', label: 'Direction', kind: 'enum', options: ['right', 'left', 'up', 'down'], default: 'right' },
      distance('Relative: empty = a quarter of the scene'),
      { key: 'fromX', label: 'From x', kind: 'number', min: -SCENE_COORD_LIMIT, max: SCENE_COORD_LIMIT, step: 1, default: null, help: 'Absolute start (jumps there)' },
      { key: 'fromY', label: 'From y', kind: 'number', min: -SCENE_COORD_LIMIT, max: SCENE_COORD_LIMIT, step: 1, default: null },
      { key: 'toX', label: 'To x', kind: 'number', min: -SCENE_COORD_LIMIT, max: SCENE_COORD_LIMIT, step: 1, default: null, help: 'Absolute end' },
      { key: 'toY', label: 'To y', kind: 'number', min: -SCENE_COORD_LIMIT, max: SCENE_COORD_LIMIT, step: 1, default: null },
    ],
  },
  sway: { label: 'Swaying', group: 'object', description: 'Pendulum rotation around the anchor', characterOnly: false, defaultSeconds: 2, cyclic: true, cycleSeconds: 2, flexible: true, defaultEasing: 'ease-in-out', params: [{ key: 'angle', label: 'Angle', kind: 'number', min: 1, max: 90, step: 1, default: 8, unit: '°' }] },
  path: {
    label: 'Moving along a path',
    group: 'object',
    description: 'Follow a line, polyline or Bézier path (optionally facing along it)',
    characterOnly: false,
    defaultSeconds: 3,
    cyclic: false,
    flexible: true,
    defaultEasing: 'ease-in-out',
    params: [],
  },
};

export const CHARACTER_TEMPLATES = TEMPLATE_IDS.filter((t) => TEMPLATES[t].group === 'character');
export const OBJECT_TEMPLATES = TEMPLATE_IDS.filter((t) => TEMPLATES[t].group === 'object');

// ---------------------------------------------------------------- Schemas

const Seconds = z.number().min(0).max(MAX_ANIMATION_SECONDS);
const Coord = z.number().min(-SCENE_COORD_LIMIT).max(SCENE_COORD_LIMIT);
export const SlotNameSchema = z.string().regex(/^[a-z][a-z0-9-]{0,31}$/, 'Slot names are lowercase letters, digits and dashes');

export const PathSchema = z
  .object({
    kind: z.enum(['linear', 'bezier']),
    /** Anchor-point positions (absolute scene px, or offsets from the start when relative). */
    points: z.array(z.tuple([Coord, Coord])).min(2).max(MAX_PATH_POINTS),
    relative: z.boolean(),
    /** Rotate to face along the path (adds to the base rotation; unwrapped, no jumps). */
    orient: z.boolean(),
    reverse: z.boolean(),
  })
  .superRefine((p, ctx) => {
    if (p.kind === 'bezier' && (p.points.length - 1) % 3 !== 0) {
      ctx.addIssue({ code: 'custom', path: ['points'], message: 'A Bézier path has 3n+1 points (start, then two controls and an end per segment)' });
    }
  });
export type MotionPath = z.infer<typeof PathSchema>;

export const TimingSchema = z.object({
  /**
   * sequence: after the previous step ends · with: starts with `ref` (default: previous) ·
   * after: after `ref` ends · at: at `at` seconds. `delay` is added in every mode.
   */
  mode: z.enum(['sequence', 'with', 'after', 'at']),
  ref: IdSchema.nullable().default(null),
  at: Seconds.nullable().default(null),
  delay: Seconds.default(0),
});
export type StepTimingSpec = z.infer<typeof TimingSchema>;

const ParamValueSchema = z.union([z.number(), z.string().max(40), z.boolean(), z.null()]);

export const MotionStepSchema = z.object({
  id: IdSchema,
  template: TemplateIdSchema,
  targets: z.array(SlotNameSchema).min(1).max(MAX_TARGETS),
  /** Seconds between successive targets' starts (staggered group motion). */
  stagger: z.number().min(0).max(10).default(0),
  timing: TimingSchema.default({ mode: 'sequence', ref: null, at: null, delay: 0 }),
  /** Total seconds; null = the template's default × repetitions (or a share of the plan total). */
  duration: z.number().positive().max(MAX_ANIMATION_SECONDS).nullable().default(null),
  repeat: z.number().int().min(1).max(MAX_REPEAT).default(1),
  /** null = the template's default easing. */
  easing: EasingSchema.nullable().default(null),
  params: z.record(z.string().max(32), ParamValueSchema).default({}),
  path: PathSchema.nullable().default(null),
  /** Seeded variation (0 = none). Clearly optional; never applied unless set. */
  variation: z.number().min(0).max(1).default(0),
  seed: z.number().int().min(0).max(2 ** 31 - 1).nullable().default(null),
});
export type MotionStep = z.infer<typeof MotionStepSchema>;
export type MotionStepInput = z.input<typeof MotionStepSchema>;

export const SlotSchema = z.object({
  name: SlotNameSchema,
  kind: z.enum(['character', 'object']),
  label: z.string().trim().min(1).max(60),
});
export type MotionSlot = z.infer<typeof SlotSchema>;

export const MotionPlanSchema = z
  .object({
    version: z.literal(MOTION_PLAN_VERSION),
    /** Base seed for steps with variation and no seed of their own. */
    seed: z.number().int().min(0).max(2 ** 31 - 1).default(1),
    /** When set, flexible steps without a duration share the time left after the others. */
    totalSeconds: z.number().positive().max(MAX_ANIMATION_SECONDS).nullable().default(null),
    slots: z.array(SlotSchema).min(1).max(MAX_TARGETS),
    steps: z.array(MotionStepSchema).min(1).max(MAX_STEPS),
  })
  .superRefine((plan, ctx) => {
    const slots = new Map(plan.slots.map((s) => [s.name, s]));
    if (slots.size !== plan.slots.length) ctx.addIssue({ code: 'custom', path: ['slots'], message: 'Slot names must be unique' });
    const seen = new Set<string>();
    plan.steps.forEach((step, i) => {
      const at = (p: (string | number)[], message: string) => ctx.addIssue({ code: 'custom', path: ['steps', i, ...p], message: `Step ${i + 1} (${TEMPLATES[step.template].label}): ${message}` });
      if (seen.has(step.id)) at(['id'], 'duplicate step id');
      const info = TEMPLATES[step.template];
      for (const t of step.targets) {
        const slot = slots.get(t);
        if (!slot) at(['targets'], `unknown target “${t}”`);
        else if (info.characterOnly && slot.kind !== 'character') at(['targets'], `needs a character, but “${slot.label}” can be any object`);
      }
      if (new Set(step.targets).size !== step.targets.length) at(['targets'], 'a target is listed twice');
      if (step.timing.ref !== null && !seen.has(step.timing.ref)) at(['timing', 'ref'], 'can only refer to an earlier step');
      if (step.timing.mode === 'at' && step.timing.at === null) at(['timing', 'at'], 'needs a start time');
      if (step.template === 'path' && !step.path) at(['path'], 'needs a path');
      if (step.template !== 'path' && step.path) at(['path'], 'only the path motion takes a path');
      const defs = new Map(info.params.map((d) => [d.key, d]));
      for (const [key, value] of Object.entries(step.params)) {
        const def = defs.get(key);
        if (!def) {
          at(['params', key], `unknown parameter “${key}”`);
          continue;
        }
        const problem = checkParam(def, value);
        if (problem) at(['params', key], problem);
      }
      seen.add(step.id);
    });
  });
export type MotionPlan = z.infer<typeof MotionPlanSchema>;
export type MotionPlanInput = z.input<typeof MotionPlanSchema>;

/** slot name → object id in the scene being animated. */
export type Binding = Record<string, string>;

function checkParam(def: ParamDef, value: ParamValue): string | null {
  if (value === null) return def.kind === 'number' && def.default === null ? null : `${def.label} is required`;
  switch (def.kind) {
    case 'number':
      if (typeof value !== 'number' || !Number.isFinite(value)) return `${def.label} must be a number`;
      if (value < def.min || value > def.max) return `${def.label} must be between ${def.min} and ${def.max}`;
      return null;
    case 'enum':
      return typeof value === 'string' && def.options.includes(value) ? null : `${def.label} must be one of ${def.options.join(', ')}`;
    case 'boolean':
      return typeof value === 'boolean' ? null : `${def.label} must be on or off`;
  }
}

/** A step's parameters with defaults filled in. */
export function resolvedParams(step: Pick<MotionStep, 'template' | 'params'>): Record<string, ParamValue> {
  const out: Record<string, ParamValue> = {};
  for (const def of TEMPLATES[step.template].params) out[def.key] = step.params[def.key] ?? def.default;
  return out;
}

/** Short human description of a step ("Running right 3 s", "Pulsing ×3"). */
export function describeStep(step: MotionStep, slotLabel: (slot: string) => string): string {
  const info = TEMPLATES[step.template];
  const p = resolvedParams(step);
  const bits: string[] = [info.label];
  if (typeof p.direction === 'string' && p.direction !== 'none' && ['walk', 'run', 'slide', 'jump', 'fall', 'spin'].includes(step.template)) bits.push(String(p.direction));
  if (step.repeat > 1) bits.push(`×${step.repeat}`);
  if (step.duration !== null) bits.push(`${round(step.duration)} s`);
  const timing = step.timing;
  const when = timing.mode === 'at' ? `at ${round(timing.at ?? 0)} s` : timing.mode === 'with' ? 'with previous' : timing.mode === 'after' ? 'after a step' : 'then';
  return `${bits.join(' ')} · ${step.targets.map(slotLabel).join(', ')} · ${when}${timing.delay ? ` +${round(timing.delay)} s` : ''}`;
}

const round = (v: number) => Math.round(v * 100) / 100;
